import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createAiSummaryQueries(client: StoxlyClient) {
  return createQueryKeys('aiSummary', {
    // ['aiSummary', 'analysis', { query }] — fox F2: cache-only structured
    // call-analysis read (generation goes through the `generateAnalysis` mutation).
    analysis: (query: Query<'aiSummaryGetAnalysis'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.aiSummaryGetAnalysis({ queries: query, signal }),
    }),

    // ['aiSummary', 'usage'] — #2010: the reader's own AI allowance for the current
    // month (both quota legs) and the tier cap it counts against. No parameters: the
    // answer is per-reader, so the key is the whole identity of the read.
    usage: {
      queryKey: null,
      queryFn: ({ signal }) => client.aiSummaryGetUsage({ signal }),
    },

    // ['aiSummary', 'analysisTones', { query }] — fox F2: persisted tone chips for
    // the IR events list (batch, cache-only).
    analysisTones: (query: Query<'aiSummaryGetAnalysisTones'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.aiSummaryGetAnalysisTones({ queries: query, signal }),
    }),

    // ['aiSummary', 'dividendSafety', { query }] — fox F5: cache-only dividend-safety
    // narrative read (generation goes through the `generateDividendSafety` mutation).
    dividendSafety: (query: Query<'aiSummaryGetDividendSafety'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.aiSummaryGetDividendSafety({ queries: query, signal }),
    }),
  });
}

export function createAiSummaryMutations(client: StoxlyClient) {
  return {
    // Fox F2: the quota-gated structured-analysis generation (lazy + durable cache) —
    // it replaced the markdown summary/stream pair.
    generateAnalysis: {
      mutationFn: (body: Body<'aiSummaryGenerateAnalysis'>) =>
        client.aiSummaryGenerateAnalysis(body),
    },
    // Fox F5: the quota-gated dividend-safety narrative generation (lazy + durable cache).
    generateDividendSafety: {
      mutationFn: (body: Body<'aiSummaryGenerateDividendSafety'>) =>
        client.aiSummaryGenerateDividendSafety(body),
    },
    streamDocumentTranslation: {
      mutationFn: (body: Body<'aiSummaryStreamDocumentTranslation'>) =>
        client.aiSummaryStreamDocumentTranslation(body),
    },
    batchTranslation: {
      mutationFn: (body: Body<'aiSummaryBatchTranslation'>) =>
        client.aiSummaryBatchTranslation(body),
    },
    checkTranslation: {
      mutationFn: (body: Body<'aiSummaryCheckTranslation'>) =>
        client.aiSummaryCheckTranslation(body),
    },
    streamTranslation: {
      mutationFn: (body: Body<'aiSummaryStreamTranslation'>) =>
        client.aiSummaryStreamTranslation(body),
    },
  };
}
